import { useMemo } from 'react';

import {
  DataPriorityControl,
  DataPriorityControlLabelsType,
} from '@hcs/design-system';
import { LoadingSpinner } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { usePatchUiPreferencesForUser } from '@hcs/huell';
import { DataPriorityCerberusStats } from '@hcs/types';
import { CerberusStatsFields } from '@hcs/types';

import { CERBERUS_STATS_CHART_LIMIT } from '../../constants';
import { useCerberusStatsFields } from '../../hooks/useCerberusStatsFields';
import {
  getCerberusStatsFieldDefinition,
  getCerberusStatsFieldLabel,
} from '../../utils/cerberusStatsFields.utils';

import { MARKET_STATS_FOR_USER_GEO_TYPE } from './marketStateChartsForUser.constants';

const dataHcName = 'cerberus-stats-chart-data-preferences';

export interface MarketStateChartsForUserProps {
  actionsPortalIdRender?: string;
  geoType:
    | typeof MARKET_STATS_FOR_USER_GEO_TYPE.ZIP
    | typeof MARKET_STATS_FOR_USER_GEO_TYPE.MSA;
  onSubmit?: (chartDataPriority: DataPriorityCerberusStats) => void;
  onCancel?: VoidFunction;
}
export const MarketStateChartsForUser = ({
  ...props
}: MarketStateChartsForUserProps) => {
  const { isPhoneWidth } = useScreenSize();
  // compute params that are zip or msa specific

  const { data: customFieldsData, isLoading: isPreferencesLoading } =
    useCerberusStatsFields(props.geoType.chartsType);
  const { mutate: patchPreferences } = usePatchUiPreferencesForUser();

  const handleSave = (updated: DataPriorityCerberusStats) => {
    // This will actually always be defined here because of the `disabled` prop on the submit button
    patchPreferences?.([
      {
        op: 'add',
        path: `/${props.geoType.preferenceKey}`,
        value: updated,
      },
    ]);
    props.onSubmit?.(updated);
  };

  const labels = useMemo(() => {
    return props.geoType.ordering.reduce<
      DataPriorityControlLabelsType<CerberusStatsFields>
    >((accum, f) => {
      accum[f] = {
        label: getCerberusStatsFieldLabel(f) || f,
        description: getCerberusStatsFieldDefinition(f),
      };
      return accum;
    }, {});
  }, [props.geoType.ordering]);

  if (isPreferencesLoading) {
    return <LoadingSpinner dataHcName={`${dataHcName}-skeleton`} />;
  }

  const handleReset = () => {
    patchPreferences?.([
      {
        op: 'remove',
        path: `/${props.geoType.preferenceKey}`,
      },
    ]);
  };

  return (
    <DataPriorityControl
      {...props}
      dragDropDisabled={isPhoneWidth}
      dataHcName={dataHcName}
      onSubmit={handleSave}
      defaultOrder={props.geoType.ordering}
      labels={labels}
      dataPreferences={
        customFieldsData
          ? {
              order: customFieldsData.order,
              inactive: customFieldsData.inactive,
            }
          : undefined
      }
      activeLimit={{
        limit: CERBERUS_STATS_CHART_LIMIT,
        limitWarning: 'The Maximum number of charts has been reached.',
      }}
      hasReset
      hasSearch
      resetTableOptionsPrefs={handleReset}
      numCols={isPhoneWidth ? 1 : 2}
    />
  );
};
